import { ChevronRight } from 'lucide-react';

import { SensitivityChip } from '../../atoms/SensitivityChip';
import { usePopoverPlacement, type PopoverAnchor } from '../../hooks';
import { DomainIcon } from '../../icons/DomainIcon';
import { cn } from '../../lib/utils';
import type { RowActionItem } from '../RowActionPopover';

export interface PipeCounts {
  files: number;
  nhi: number;
  hi: number;
  external: number;
}

export interface PipeBreakdownItem {
  /** API id; chip tint resolved from this. */
  id: string;
  label: string;
  count: number;
}

export interface PipePopoverProps {
  /** Source-identity label (left side of the chevron breadcrumb). */
  fromLabel: string;
  /** Target data-context label (right side of the chevron breadcrumb). */
  toLabel: string;
  counts: PipeCounts;
  sensitivityBreakdown: readonly PipeBreakdownItem[];
  regulationBreakdown: readonly PipeBreakdownItem[];
  formatNumber: (value: number) => string;
  /** Two-action list at the bottom of the popover. */
  actions: readonly RowActionItem[];
  /** The cursor point on the pipe (a zero-size rect); the popover opens below-start of it. */
  anchor: PopoverAnchor;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  className?: string;
}

export function PipePopover({
  fromLabel,
  toLabel,
  counts,
  sensitivityBreakdown,
  regulationBreakdown,
  formatNumber,
  actions,
  anchor,
  onMouseEnter,
  onMouseLeave,
  className,
}: PipePopoverProps) {
  const { ref, style } = usePopoverPlacement(anchor);

  return (
    <div
      ref={ref}
      data-testid="pipe-popover"
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        // Figma `5:20339`: 217 wide, 20px padding, and exactly one divider —
        // above the action list. Header, counts and breakdown are separated by
        // 16px of space, not by rules.
        'flex min-w-[217px] flex-col overflow-hidden rounded-lg border border-grey-200 bg-white p-5 shadow-lg',
        className
      )}
    >
      {/* Chevron breadcrumb header */}
      <div className="flex items-center gap-1">
        <span className="text-[13px] font-medium leading-[14px] text-dawn-black">{fromLabel}</span>
        <ChevronRight className="h-3 w-3 shrink-0 text-grey-400" aria-hidden="true" />
        <span className="text-[13px] font-medium leading-[14px] text-dawn-black">{toLabel}</span>
      </div>

      {/* Count row */}
      <div className="mt-4 flex items-center gap-4">
        {/* Glyphs are the canonical domain atoms, matching the data-context row
            and Figma `5:20345`: files, the NHI bot, a single HI person, and the
            same person in red for external — not a warning triangle, which reads
            as an alert rather than an identity type. NHI · HI · External follows
            the data-context row's order; NHI always shows, so a machine-only
            pipe never reads as "nobody accessed this" (TDEV-1355). */}
        <span className="flex items-center gap-1 text-[12px] leading-3 text-dawn-black tabular-nums">
          <DomainIcon name="files" size={12} className="shrink-0" />
          {formatNumber(counts.files)}
        </span>
        <span
          data-testid="pipe-nhi-count"
          className="flex items-center gap-1 text-[12px] leading-3 text-dawn-black tabular-nums"
        >
          <DomainIcon name="nonHumanIdentity" size={12} className="shrink-0" />
          {formatNumber(counts.nhi)}
        </span>
        <span className="flex items-center gap-1 text-[12px] leading-3 text-dawn-black tabular-nums">
          <DomainIcon name="humanIdentity" size={12} className="shrink-0" />
          {formatNumber(counts.hi)}
        </span>
        {/* Unlike a data-context row, which drops the glyph entirely at zero,
            the pipe popover always states the external count — "0 external" is
            the reassurance the user opened the pipe for (Figma `5:20353`,
            `5:19169`). */}
        <span
          data-testid="pipe-external-count"
          className="flex items-center gap-1 text-[12px] leading-3 text-error tabular-nums"
        >
          <DomainIcon name="externalIdentity" size={12} className="shrink-0" />
          {formatNumber(counts.external)}
        </span>
      </div>

      {/* Sensitivity breakdown */}
      {sensitivityBreakdown.length > 0 && (
        <div data-testid="pipe-sensitivity-breakdown" className="mt-4 flex flex-col gap-2">
          {sensitivityBreakdown.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2">
              <SensitivityChip level={item.id} label={item.label} />
              <span className="flex items-center gap-1 text-[12px] tabular-nums text-dawn-black">
                <DomainIcon name="files" size={12} className="shrink-0" />
                {formatNumber(item.count)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Regulation breakdown */}
      {regulationBreakdown.length > 0 && (
        <div data-testid="pipe-regulation-breakdown" className="mt-2 flex flex-col gap-2">
          {regulationBreakdown.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-dawn-black">{item.label}</span>
              <span className="flex items-center gap-1 text-[12px] tabular-nums text-dawn-black">
                <DomainIcon name="files" size={12} className="shrink-0" />
                {formatNumber(item.count)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Action list */}
      <div className="my-4 h-px bg-grey-100" aria-hidden="true" />
      <div className="flex flex-col gap-4">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              type="button"
              onClick={action.onClick}
              className="-mx-5 -my-1 flex items-center gap-1 px-5 py-1 text-left text-[12px] leading-3 text-dawn-black hover:bg-grey-50"
            >
              <Icon className="h-3 w-3 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

PipePopover.displayName = 'PipePopover';
